import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../components/api/axiosInstance";
import TasksTable from "./TasksTable";
import { fetchAllUserStories } from "./userStoryService";
import "../assets/css/DigixLeads.css";
import "../assets/css/Projects.css";
import "../assets/css/Tasks.css";

async function fetchSubprojectsForProject(projectId) {
  if (!projectId) return [];
  let response = await axiosInstance.get(`/sub-projects/project/${projectId}`);
  let raw = response.data;
  let arr = [];
  let primaryParsed = false;
  if (Array.isArray(raw)) {
    arr = raw;
    primaryParsed = true;
  } else if (raw && Array.isArray(raw.sub_projects)) {
    arr = raw.sub_projects;
    primaryParsed = true;
  } else if (raw && Array.isArray(raw.data)) {
    arr = raw.data;
    primaryParsed = true;
  }
  if (!primaryParsed) {
    try {
      response = await axiosInstance.get(`/projects/${projectId}/sub-projects`);
      raw = response.data;
      arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
    } catch {
      arr = [];
    }
  }
  return (arr || [])
    .map((sp) => ({
      id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
      name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? "",
    }))
    .filter((x) => x.id != null && String(x.name).trim() !== "")
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

function TasksPage({ projects = [], loading = false, onFetchProjects }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [tasksReady, setTasksReady] = useState(false);
  const [projectsRequested, setProjectsRequested] = useState(false);
  const [dashboardTaskStatusPreset, setDashboardTaskStatusPreset] = useState("");

  const [selectedProjectId, setSelectedProjectId] = useState("");
  const selectedProject = useMemo(
    () =>
      projects.find(
        (p) => String(p.Project_Id ?? p.project_id ?? p.id) === String(selectedProjectId)
      ) || null,
    [projects, selectedProjectId]
  );
  const selectedProjectName =
    selectedProject?.Project_Name ?? selectedProject?.project_name ?? selectedProject?.name ?? "";

  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);
  const [selectedSubprojectId, setSelectedSubprojectId] = useState("");
  const selectedSubprojectName = useMemo(() => {
    if (!selectedSubprojectId) return "";
    const sp = subprojects.find((s) => String(s.id) === String(selectedSubprojectId));
    return sp?.name || "";
  }, [selectedSubprojectId, subprojects]);

  const [userStoriesForTasks, setUserStoriesForTasks] = useState([]);
  const [loadingUserStoriesForTasks, setLoadingUserStoriesForTasks] = useState(false);
  const [selectedStoryId, setSelectedStoryId] = useState("");
  const selectedStoryTitle = useMemo(() => {
    const st = userStoriesForTasks.find((s) => String(s.id) === String(selectedStoryId));
    return st?.title || "";
  }, [selectedStoryId, userStoriesForTasks]);

  const allStoriesCacheRef = useRef(null);

  useEffect(() => {
    const s = location.state;
    if (!s || typeof s !== "object") return;
    let applied = false;
    if (s.resetTaskFilters) {
      setSelectedProjectId("");
      setSelectedSubprojectId("");
      setSelectedStoryId("");
      applied = true;
    }
    if (typeof s.dashboardTaskStatusPreset === "string") {
      setDashboardTaskStatusPreset(s.dashboardTaskStatusPreset);
      applied = true;
    }
    if (applied) navigate(".", { replace: true, state: null });
  }, [location.state, navigate]);

  useEffect(() => {
    setSelectedSubprojectId("");
    setSelectedStoryId("");
    let cancelled = false;
    const load = async () => {
      if (!selectedProjectId) {
        setSubprojects([]);
        return;
      }
      setLoadingSubprojects(true);
      try {
        const mapped = await fetchSubprojectsForProject(selectedProjectId);
        if (!cancelled) setSubprojects(mapped);
      } catch (err) {
        console.error("Error fetching subprojects:", err);
        if (!cancelled) setSubprojects([]);
      } finally {
        if (!cancelled) setLoadingSubprojects(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId]);

  useEffect(() => {
    if (!onFetchProjects) return;
    if (!tasksReady || projectsRequested) return;
    onFetchProjects({});
    setProjectsRequested(true);
  }, [tasksReady, onFetchProjects, projectsRequested]);

  useEffect(() => {
    if (!tasksReady) return;
    if (!projectsRequested) return;
    if (loading) return;

    const mapStories = (list) =>
      (list || [])
        .map((st) => ({
          ...st,
          id: st.User_Story_Id ?? st.user_story_id ?? st.id,
          title: st.Title ?? st.title ?? st.User_Story_Title ?? st.user_story_title ?? "—",
          projectId: st.Project_Id ?? st.project_id ?? st.projectId ?? null,
          subProjectId: st.Sub_Project_Id ?? st.sub_project_id ?? st.subProjectId ?? null,
        }))
        .filter((x) => x.id != null);

    const applyStories = (list) => {
      let mapped = mapStories(list);
      const visibleProjectIds = new Set(
        (projects || [])
          .map((p) => String(p.Project_Id ?? p.project_id ?? p.id))
          .filter(Boolean)
      );
      if (selectedProjectId) {
        mapped = mapped.filter((st) => String(st.projectId) === String(selectedProjectId));
      } else {
        mapped = mapped.filter((st) => visibleProjectIds.has(String(st.projectId)));
      }
      if (selectedSubprojectId) {
        mapped = mapped.filter((st) => String(st.subProjectId) === String(selectedSubprojectId));
      }
      mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
      setUserStoriesForTasks(mapped);
    };

    if (allStoriesCacheRef.current) {
      applyStories(allStoriesCacheRef.current);
      return;
    }

    let cancelled = false;
    const loadStories = async () => {
      setLoadingUserStoriesForTasks(true);
      try {
        setSelectedStoryId("");
        const list = await fetchAllUserStories();
        if (cancelled) return;
        allStoriesCacheRef.current = Array.isArray(list) ? list : [];
        applyStories(allStoriesCacheRef.current);
      } catch (err) {
        console.error("Error loading stories for tasks:", err);
        if (!cancelled) {
          allStoriesCacheRef.current = [];
          setUserStoriesForTasks([]);
          setSelectedStoryId("");
        }
      } finally {
        if (!cancelled) setLoadingUserStoriesForTasks(false);
      }
    };
    loadStories();
    return () => {
      cancelled = true;
    };
  }, [
    tasksReady,
    projectsRequested,
    loading,
    selectedProjectId,
    selectedSubprojectId,
    projects,
  ]);

  return (
    <div className="projects-container project-management-container">
      <div className="main-card project-management-main-card">
        <div
          className="project-management-tab-content project-management-tasks-tab"
          style={{ paddingTop: 0 }}
          aria-label="Tasks"
        >
          <TasksTable
            dataActive
            onTasksFetched={() => setTasksReady(true)}
            storyId={selectedStoryId ? Number(selectedStoryId) : null}
            projectId={selectedProjectId ? Number(selectedProjectId) : null}
            subProjectId={selectedSubprojectId ? Number(selectedSubprojectId) : null}
            storyTitle={selectedStoryTitle}
            projectName={selectedProjectName}
            subProjectName={selectedSubprojectName}
            projects={projects}
            subprojects={subprojects}
            loadingSubprojects={loadingSubprojects}
            selectedProjectIdValue={selectedProjectId}
            selectedSubprojectIdValue={selectedSubprojectId}
            onProjectChange={(val) => {
              setSelectedProjectId(val);
              if (!val) setSelectedSubprojectId("");
            }}
            onSubprojectChange={setSelectedSubprojectId}
            selectedStoryIdValue={selectedStoryId}
            onStoryChange={setSelectedStoryId}
            loadingUserStoriesForTasks={loadingUserStoriesForTasks}
            storiesForAll={userStoriesForTasks}
            allStoriesSelected={!selectedStoryId}
            dashboardTaskStatusPreset={dashboardTaskStatusPreset}
          />
        </div>
      </div>
    </div>
  );
}

export default TasksPage;
